import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import PrintIcon from '@mui/icons-material/Print';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import { collection, query, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';

const STATUS_LABELS = {
  'pending': 'En attente',
  'in_progress': 'En cours',
  'En attente': 'En attente',
  'En cours': 'En cours',
  'Terminé': 'Terminé',
  'completed': 'Terminé'
};

const STATUS_COLORS = {
  'pending': 'warning',
  'in_progress': 'info',
  'En attente': 'warning',
  'En cours': 'info',
  'Terminé': 'success',
  'completed': 'success'
};

const PRIORITY_COLORS = {
  'urgent': 'error',
  'high': 'warning',
  'medium': 'info',
  'low': 'success'
};

const PRIORITY_LABELS = {
  'urgent': 'Urgente', 'high': 'Haute', 'medium': 'Moyenne', 'low': 'Basse'
};

const AllRequestsTab = () => {
  const navigate = useNavigate();
  const { userSchool } = useAuth();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  
  // Services disponibles
  const [services, setServices] = useState([]);
  
  // Sélection multiple
  const [selected, setSelected] = useState([]);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  
  // Dialog de détails
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  useEffect(() => {
    fetchRequests();
    fetchServices();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, searchTerm, statusFilter, priorityFilter, serviceFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      
      console.log('AllRequestsTab: Récupération des demandes');

      // Récupérer toutes les demandes
      const q = query(collection(db, COLLECTIONS.WORK_REQUESTS));
      const querySnapshot = await getDocs(q);
      console.log('AllRequestsTab: Nombre de demandes trouvées:', querySnapshot.size);
      
      const requestsData = await Promise.all(
        querySnapshot.docs.map(async (docSnapshot) => {
          const data = docSnapshot.data();
          const enrichedData = {
            id: docSnapshot.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date()
          };

          // Récupérer le nom du service demandeur
          if (data.requestingServiceId || data.createdBy) {
            try {
              const requesterId = data.requestingServiceId || data.createdBy;
              
              // Essayer d'abord dans la collection SERVICES
              let requesterDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, requesterId));
              
              if (requesterDoc.exists()) {
                const requesterData = requesterDoc.data();
                enrichedData.createdByName = requesterData.name || requesterData.displayName || 'Service';
              } else {
                // Si non trouvé, essayer dans la collection users
                const userDoc = await getDoc(doc(db, 'users', requesterId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  enrichedData.createdByName = userData.displayName || userData.name || userData.email || 'Utilisateur';
                }
              }
            } catch (error) {
              console.error('Erreur récupération demandeur:', error);
            }
          }

          // Récupérer le nom du service technique si assigné
          if (data.assignedServiceId) {
            try {
              // Essayer d'abord dans la collection SERVICES
              let serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, data.assignedServiceId));
              
              if (serviceDoc.exists()) {
                const serviceData = serviceDoc.data();
                enrichedData.assignedServiceName = serviceData.name || serviceData.displayName || 'Service technique';
              } else {
                // Si non trouvé, essayer dans la collection users
                const userDoc = await getDoc(doc(db, 'users', data.assignedServiceId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  enrichedData.assignedServiceName = userData.displayName || userData.name || userData.email || 'Service technique';
                }
              }
            } catch (error) {
              console.error('Erreur récupération service:', error);
            }
          }

          // Récupérer les infos du technicien depuis les interventions
          if (data.id || docSnapshot.id) {
            try {
              const interventionsQuery = query(
                collection(db, COLLECTIONS.INTERVENTIONS),
                where('workRequestId', '==', docSnapshot.id)
              );
              const interventionsSnapshot = await getDocs(interventionsQuery);
              
              if (!interventionsSnapshot.empty) {
                const intervention = interventionsSnapshot.docs[0].data();
                enrichedData.technicianName = intervention.technicianName || null;
              }
            } catch (error) {
              console.error('Erreur récupération intervention:', error);
            }
          }

          return enrichedData;
        })
      );

      console.log('AllRequestsTab: Demandes enrichies:', requestsData);

      // Trier par date décroissante
      requestsData.sort((a, b) => b.createdAt - a.createdAt);

      setRequests(requestsData);
      setFilteredRequests(requestsData);
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      console.log('AllRequestsTab: Récupération des services');

      // Essayer d'abord la collection SERVICES
      let q = query(collection(db, COLLECTIONS.SERVICES));
      let querySnapshot = await getDocs(q);
      console.log('AllRequestsTab: Nombre de services trouvés dans SERVICES:', querySnapshot.size);
      
      let servicesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Service trouvé:', { id: doc.id, data });
        return {
          id: doc.id,
          name: data.name || data.displayName || data.email || 'Service sans nom',
          ...data
        };
      });

      // Si aucun service trouvé, essayer la collection USERS avec role service
      if (servicesData.length === 0) {
        console.log('AllRequestsTab: Tentative de récupération depuis USERS');
        q = query(collection(db, 'users'), where('role', '==', 'service'));
        querySnapshot = await getDocs(q);
        console.log('AllRequestsTab: Nombre de services trouvés dans USERS:', querySnapshot.size);
        
        servicesData = querySnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Service trouvé dans users:', { id: doc.id, data });
          return {
            id: doc.id,
            name: data.displayName || data.email || 'Service sans nom',
            ...data
          };
        });
      }

      console.log('AllRequestsTab: Services finaux récupérés:', servicesData);
      setServices(servicesData);
    } catch (error) {
      console.error('Erreur lors de la récupération des services:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Filtre de recherche
    if (searchTerm) {
      filtered = filtered.filter(req =>
        req.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.problem?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.createdByName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtre de statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }

    // Filtre de priorité
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(req => req.priority === priorityFilter);
    }

    // Filtre de service
    if (serviceFilter !== 'all') {
      filtered = filtered.filter(req => 
        req.requestingServiceId === serviceFilter || 
        req.assignedServiceId === serviceFilter
      );
    }

    setFilteredRequests(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = filteredRequests.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  const exportToExcel = () => {
    const dataToExport = (selected.length > 0 
      ? filteredRequests.filter(req => selected.includes(req.id))
      : filteredRequests
    ).map(req => ({
      'Titre': req.title || 'Sans titre',
      'Description': req.description || req.problem || '',
      'Service demandeur': req.createdByName || 'Non spécifié',
      'Service technique': req.assignedServiceName || 'Non assigné',
      'Technicien': req.technicianName || 'Non assigné',
      'Statut': STATUS_LABELS[req.status] || req.status,
      'Priorité': PRIORITY_LABELS[req.priority] || req.priority || 'Non définie',
      'Localisation': req.location?.building || req.location || 'Non spécifiée',
      'Date de création': format(req.createdAt, 'dd/MM/yyyy HH:mm', { locale: fr })
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Demandes');
    XLSX.writeFile(wb, `demandes_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  const handlePrint = () => {
    const requestsToPrint = selected.length > 0 
      ? filteredRequests.filter(req => selected.includes(req.id))
      : filteredRequests;
    
    if (requestsToPrint.length === 0) {
      alert('Aucune demande à imprimer');
      return;
    }
    
    setPrintDialogOpen(true);
  };

  const printRequests = () => {
    const requestsToPrint = selected.length > 0 
      ? filteredRequests.filter(req => selected.includes(req.id))
      : filteredRequests;

    const printWindow = window.open('', '', 'height=800,width=1000');
    printWindow.document.write('<html><head><title>Impression des demandes</title>');
    printWindow.document.write(`
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #1976d2; border-bottom: 3px solid #1976d2; padding-bottom: 10px; }
        .header { text-align: center; margin-bottom: 30px; }
        .request { 
          border: 1px solid #ddd; 
          padding: 15px; 
          margin-bottom: 20px; 
          page-break-inside: avoid;
          border-radius: 8px;
        }
        .request-header { 
          background-color: #f5f5f5; 
          padding: 10px; 
          margin: -15px -15px 15px -15px;
          border-radius: 8px 8px 0 0;
        }
        .request-title { font-size: 18px; font-weight: bold; color: #1976d2; }
        .field { margin: 8px 0; }
        .field-label { font-weight: bold; color: #555; }
        .field-value { margin-left: 10px; }
        .status { 
          display: inline-block; 
          padding: 4px 12px; 
          border-radius: 12px; 
          font-size: 12px; 
          font-weight: bold;
        }
        .status-pending { background-color: #fff3cd; color: #856404; }
        .status-inProgress { background-color: #d1ecf1; color: #0c5460; }
        .status-completed { background-color: #d4edda; color: #155724; }
        .footer { 
          margin-top: 30px; 
          text-align: center; 
          font-size: 12px; 
          color: #666;
          border-top: 1px solid #ddd;
          padding-top: 10px;
        }
        @media print {
          .no-print { display: none; }
        }
      </style>
    `);
    printWindow.document.write('</head><body>');
    printWindow.document.write('<div class="header">');
    printWindow.document.write('<h1>📋 Liste des demandes de travaux</h1>');
    printWindow.document.write(`<p>Date d'impression: ${format(new Date(), 'dd/MM/yyyy à HH:mm', { locale: fr })}</p>`);
    printWindow.document.write(`<p>Nombre de demandes: ${requestsToPrint.length}</p>`);
    printWindow.document.write('</div>');

    requestsToPrint.forEach((req, index) => {
      const statusClass = req.status === 'completed' || req.status === 'Terminé' ? 'status-completed' :
                         req.status === 'in_progress' || req.status === 'En cours' ? 'status-inProgress' : 'status-pending';
      
      printWindow.document.write('<div class="request">');
      printWindow.document.write('<div class="request-header">');
      printWindow.document.write(`<div class="request-title">${index + 1}. ${req.title || 'Sans titre'}</div>`);
      printWindow.document.write('</div>');
      
      printWindow.document.write(`<div class="field"><span class="field-label">📅 Date de création:</span><span class="field-value">${format(req.createdAt, 'dd/MM/yyyy à HH:mm', { locale: fr })}</span></div>`);
      printWindow.document.write(`<div class="field"><span class="field-label">👤 Service demandeur:</span><span class="field-value">${req.createdByName || 'Non spécifié'}</span></div>`);
      printWindow.document.write(`<div class="field"><span class="field-label">🏢 Service technique:</span><span class="field-value">${req.assignedServiceName || 'Non assigné'}</span></div>`);
      if (req.technicianName) {
        printWindow.document.write(`<div class="field"><span class="field-label">👨‍🔧 Technicien assigné:</span><span class="field-value">${req.technicianName}</span></div>`);
      }
      printWindow.document.write(`<div class="field"><span class="field-label">📍 Localisation:</span><span class="field-value">${typeof req.location === 'string' ? req.location : req.location?.building || 'Non spécifiée'}</span></div>`);
      printWindow.document.write(`<div class="field"><span class="field-label">📊 Statut:</span><span class="field-value"><span class="status ${statusClass}">${STATUS_LABELS[req.status] || req.status}</span></span></div>`);
      if (req.priority) {
        printWindow.document.write(`<div class="field"><span class="field-label">⚡ Priorité:</span><span class="field-value">${req.priority}</span></div>`);
      }
      if (req.description || req.problem) {
        printWindow.document.write(`<div class="field"><span class="field-label">📝 Description:</span><span class="field-value">${req.description || req.problem}</span></div>`);
      }
      printWindow.document.write('</div>');
    });

    printWindow.document.write('<div class="footer">');
    printWindow.document.write('<p>Document généré automatiquement par EduWorks</p>');
    printWindow.document.write('</div>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
    setPrintDialogOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* En-tête avec bouton Nouvelle demande */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Liste des demandes
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/nouvelle-demande')}
        >
          Nouvelle demande
        </Button>
      </Box>

      {/* Filtres */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="pending">En attente</MenuItem>
                <MenuItem value="En attente">En attente</MenuItem>
                <MenuItem value="in_progress">En cours</MenuItem>
                <MenuItem value="En cours">En cours</MenuItem>
                <MenuItem value="completed">Terminé</MenuItem>
                <MenuItem value="Terminé">Terminé</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priorité</InputLabel>
              <Select
                value={priorityFilter}
                label="Priorité"
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
                <MenuItem value="high">Élevé</MenuItem>
                <MenuItem value="medium">Moyen</MenuItem>
                <MenuItem value="low">Faible</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Service</InputLabel>
              <Select
                value={serviceFilter}
                label="Service"
                onChange={(e) => setServiceFilter(e.target.value)}
              >
                <MenuItem value="all">Tous les services</MenuItem>
                {services.length > 0 ? (
                  services.map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Aucun service disponible</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Exporter
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FilterListIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              {filteredRequests.length} demande(s) trouvée(s) sur {requests.length} au total
            </Typography>
          </Box>
          
          {selected.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={`${selected.length} sélectionnée(s)`} 
                color="primary" 
                onDelete={() => setSelected([])}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Imprimer
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setSelected([])}
              >
                Désélectionner
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < filteredRequests.length}
                  checked={filteredRequests.length > 0 && selected.length === filteredRequests.length}
                  onChange={handleSelectAllClick}
                  inputProps={{ 'aria-label': 'Sélectionner toutes les demandes' }}
                />
              </TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Service demandeur</TableCell>
              <TableCell>Service technique</TableCell>
              <TableCell>Technicien assigné</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Priorité</TableCell>
              <TableCell>Localisation</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((request) => {
                const isItemSelected = isSelected(request.id);
                return (
                  <TableRow 
                    key={request.id} 
                    hover
                    onClick={(event) => handleClick(event, request.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        inputProps={{ 'aria-labelledby': request.id }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {request.title || 'Sans titre'}
                      </Typography>
                      {request.description && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {request.description.substring(0, 50)}
                          {request.description.length > 50 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {request.createdByName || 'Non spécifié'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.assignedServiceName ? (
                        <Chip 
                          label={request.assignedServiceName} 
                          size="small" 
                          color="secondary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Non assigné
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {request.technicianName ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {request.technicianName}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[request.status] || request.status}
                        color={STATUS_COLORS[request.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {request.priority && (
                        <Chip
                          label={PRIORITY_LABELS[request.priority] || request.priority}
                          color={PRIORITY_COLORS[request.priority] || 'default'}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {typeof request.location === 'string' 
                        ? request.location 
                        : request.location?.building || 'Non spécifiée'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(request.createdAt, 'dd/MM/yyyy', { locale: fr })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(request.createdAt, 'HH:mm', { locale: fr })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Voir les détails">
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(request);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRequests.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </TableContainer>

      {/* Dialog de confirmation d'impression */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PrintIcon color="primary" />
            <Typography variant="h6">Impression des demandes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Vous êtes sur le point d'imprimer {selected.length > 0 ? selected.length : filteredRequests.length} demande(s).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Une nouvelle fenêtre va s'ouvrir avec un aperçu avant impression.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={printRequests} 
            variant="contained" 
            startIcon={<PrintIcon />}
            color="primary"
          >
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de détails de la demande */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VisibilityIcon color="primary" />
            <Typography variant="h6">Détails de la demande</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Titre</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedRequest.title || 'Sans titre'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">
                  {selectedRequest.description || selectedRequest.problem || 'Aucune description'}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Service demandeur</Typography>
                  <Typography variant="body1">
                    {selectedRequest.createdByName || 'Non spécifié'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Service technique</Typography>
                  <Typography variant="body1">
                    {selectedRequest.assignedServiceName || 'Non assigné'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Technicien assigné</Typography>
                  <Typography variant="body1">
                    {selectedRequest.technicianName || 'Non assigné'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Localisation</Typography>
                  <Typography variant="body1">
                    {typeof selectedRequest.location === 'string' 
                      ? selectedRequest.location 
                      : selectedRequest.location?.building || 'Non spécifiée'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Statut</Typography>
                  <Chip
                    label={STATUS_LABELS[selectedRequest.status] || selectedRequest.status}
                    color={STATUS_COLORS[selectedRequest.status] || 'default'}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Priorité</Typography>
                  {selectedRequest.priority ? (
                    <Chip
                      label={PRIORITY_LABELS[selectedRequest.priority] || selectedRequest.priority}
                      color={PRIORITY_COLORS[selectedRequest.priority] || 'default'}
                      size="small"
                    />
                  ) : (
                    <Typography variant="body1">Non définie</Typography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">Date de création</Typography>
                  <Typography variant="body1">
                    {format(selectedRequest.createdAt, 'dd/MM/yyyy à HH:mm', { locale: fr })}
                  </Typography>
                </Grid>

                {selectedRequest.category && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Catégorie</Typography>
                    <Typography variant="body1">{selectedRequest.category}</Typography>
                  </Grid>
                )}

                {selectedRequest.equipment && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Équipement</Typography>
                    <Typography variant="body1">{selectedRequest.equipment}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllRequestsTab;
